import { useState } from "react";
import { toast } from "sonner";
import {
  Instagram,
  Mail,
  MapPin,
  MessageCircle,
  Send,
} from "lucide-react";

import useSEO from "../lib/useSEO";
import { BRAND, buildWhatsAppLink } from "../lib/constants";
import { submitContact } from "../lib/api";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.42a8.16 8.16 0 0 0 4.77 1.52V6.62a4.85 4.85 0 0 1-1.84-.07Z" />
  </svg>
);

const CATEGORY_OPTIONS = [
  "Jersey Futsal Custom",
  "Jaket Anime Custom",
  "Kimono Haori Jepang",
  "Racing Apparel",
  "Crop Top & Streetwear",
  "Lainnya",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    category: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useSEO({
    title: "Kontak — Hubungi Tim Ace Sublimasi Nusantara",
    description:
      "Hubungi Ace Sublimasi Nusantara via WhatsApp, Instagram, TikTok, atau email. Workshop kami di Wonosobo, melayani seluruh Indonesia.",
    canonical: window.location.origin + "/kontak",
  });

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Pesan terkirim — admin akan reply via email/WhatsApp.");
      setForm({ name: "", email: "", phone: "", category: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Gagal kirim pesan. Cek koneksi dan coba lagi."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="border-b border-[#1f1f1f]">
      <section className="border-b border-[#1f1f1f] py-16 md:py-24 bg-[#070707]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
            / Kontak
          </p>
          <h1 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] text-white">
            Mari <span className="text-[#CCFF00]">Bicara.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-[#C2C2C2]">
            WhatsApp paling cepat. Tapi kalau kamu tipe yang suka detail, isi form di bawah —
            tim kami akan balas hari yang sama.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid md:grid-cols-12 gap-10">
        {/* Form */}
        <div className="md:col-span-7">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-4">
            / Form Pesan
          </p>
          <h2 className="font-display text-3xl sm:text-4xl uppercase text-white mb-8">
            Kirim Brief Atau Pertanyaan
          </h2>
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                  Nama Lengkap
                </label>
                <input
                  name="name"
                  required
                  minLength={2}
                  data-testid="contact-name"
                  value={form.name}
                  onChange={onChange}
                  className="mt-2 w-full bg-transparent border border-[#262626] focus:border-[#CCFF00] outline-none text-white px-4 py-3 transition-colors"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                  No. WhatsApp
                </label>
                <input
                  name="phone"
                  required
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="08xx-xxxx-xxxx"
                  className="mt-2 w-full bg-transparent border border-[#262626] focus:border-[#CCFF00] outline-none text-white px-4 py-3 transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                data-testid="contact-email"
                value={form.email}
                onChange={onChange}
                className="mt-2 w-full bg-transparent border border-[#262626] focus:border-[#CCFF00] outline-none text-white px-4 py-3 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Kategori (opsional)
              </label>
              <select
                name="category"
                data-testid="contact-category"
                value={form.category}
                onChange={onChange}
                className="mt-2 w-full bg-[#0a0a0a] border border-[#262626] focus:border-[#CCFF00] outline-none text-white px-4 py-3 transition-colors"
              >
                <option value="">Pilih kategori produk</option>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Pesan / Brief Singkat
              </label>
              <textarea
                name="message"
                rows={5}
                required
                minLength={5}
                data-testid="contact-message"
                value={form.message}
                onChange={onChange}
                placeholder="Ceritakan singkat: tim kamu, jumlah, tipe produk, deadline..."
                className="mt-2 w-full bg-transparent border border-[#262626] focus:border-[#CCFF00] outline-none text-white px-4 py-3 transition-colors resize-y"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-wait"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Mengirim..." : "Kirim Pesan"}
            </button>
            <p className="text-xs text-[#A3A3A3]">
              Atau langsung{" "}
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                className="text-[#CCFF00] underline"
              >
                chat admin di WhatsApp
              </a>{" "}
              untuk respons cepat.
            </p>
          </form>
        </div>

        {/* Info */}
        <div className="md:col-span-5">
          <div className="border border-[#262626] divide-y divide-[#262626] bg-[#070707]">
            <div className="p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                WhatsApp (Direkomendasikan)
              </p>
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-info-wa"
                className="font-display text-2xl uppercase text-white mt-2 inline-flex items-center gap-2 hover:text-[#CCFF00]"
              >
                <MessageCircle className="w-5 h-5" />
                {BRAND.whatsappDisplay}
              </a>
              <p className="mt-2 text-xs text-[#A3A3A3]">
                Online setiap hari · Balas cepat dalam menit
              </p>
            </div>
            <div className="p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Email
              </p>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-info-email"
                className="text-base text-white mt-2 inline-flex items-center gap-2 hover:text-[#CCFF00]"
              >
                <Mail className="w-4 h-4" />
                {BRAND.email}
              </a>
            </div>
            <div className="p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Workshop
              </p>
              <p className="mt-2 text-sm text-white inline-flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-[#CCFF00] shrink-0" />
                <span>
                  {BRAND.address.line1}
                  <br />
                  {BRAND.address.line2}
                  <br />
                  {BRAND.address.city}, {BRAND.address.province} {BRAND.address.postal}
                </span>
              </p>
            </div>
            <div className="p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
                Sosial Media
              </p>
              <div className="mt-3 flex gap-3">
                <a
                  href={`https://instagram.com/${BRAND.ig}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-ig"
                  className="w-10 h-10 inline-flex items-center justify-center border border-[#262626] text-white hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
                <a
                  href={`https://tiktok.com/@${BRAND.tiktok}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-tiktok"
                  className="w-10 h-10 inline-flex items-center justify-center border border-[#262626] text-white hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors"
                  aria-label="TikTok"
                >
                  <TikTokIcon />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6 border border-[#262626] aspect-[4/3] overflow-hidden">
            <iframe
              title="map"
              src="https://www.google.com/maps?q=Andongsili+Mojotengah+Wonosobo&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.4) invert(0.92) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
